<template>
  <sortable-list
    :items="costs"
    :order="costs.map((cost) => cost.elementUid)"
    item-key="elementUid"
    line-class="cost-item__background"
    @update:order="updateOrder($event)"
  >
    <template #default="{ item: cost }">
      <div
        class="cost-item -disabled"
        :class="{
          '-focussed': focussedCost?.elementUid == cost.elementUid
        }"
        @contextmenu.prevent="onCostContextMenu($event as PointerEvent, cost)"
        @click="onCostClick($event, cost)"
      >
        <div class="cost-item__header">
          <button
            :ref="(el) => (focusButtonRefs[cost.elementUid] = el as HTMLElement)"
            class="cost-item__title"
            type="button"
            :disabled="true"
            @focus="focussedCost = cost"
            @blur="focussedCost = null"
          >
            <span class="cost-item__titletext">
              {{ elements.byUid[cost.elementUid]?.title }}
            </span>
          </button>
          <documentation-anchor :object="action" :prop="`costs.${cost.elementUid}`" class="cost-item__docanchor ml-auto" />
          <button
            :title="$t('actions.openContextMenu')"
            class="button-icon button-icon_small cost-item__contextbutton"
            type="button"
            @click="onCostContextMenu($event as PointerEvent, cost)"
          >
            <lucide-icon name="more-horizontal" />
          </button>
        </div>

        <div class="costlist__buttons">
          <expression-input
            class="costlist__input"
            :ref="(el: unknown) => costExpressionFields[cost.elementUid] = (el as ExpressionInputElement)"
            :expression="cost.expression"
            :parse-context="{
              targets,
              resources,
              indices,
              affectedId: splitUid(cost.elementUid).id,
              affectedType: splitUid(cost.elementUid).type
            }"
            default-sign="-"
            @update:expression="updateImpulseExpression(cost, $event)"
            @click="onCostClick($event, null)"
          />
        </div>
      </div>
    </template>

    <template #after-list>
      <item-combo-select
        key="costlist__select"
        :selection="affectedItems"
        :targets="targets"
        :resources="resources"
        :indices="indices"
        :hide-selected="true"
        :types="['resource', 'target']"
        @select-single="onAffectedItemChoose"
        @choose="onAffectedItemsChoose"
      />
      <slot></slot>
    </template>
  </sortable-list>

  <context-menu
    :visible="contextMenuData.cost != null"
    :x="contextMenuData.position.x"
    :y="contextMenuData.position.y"
    @close="contextMenuData.cost = null"
  >
    <button
      class="context-menu__entry"
      type="button"
      @click="toClipboard(contextMenuData.cost)"
    >
      {{ $t("actions.copyCost") }}
    </button>
    <button
      class="context-menu__entry context-menu__entry--danger"
      type="button"
      @click="onContextmenuDelete(contextMenuData.cost)"
    >
      {{ $t("actions.deleteCost") }}
    </button>
  </context-menu>
</template>

<script setup lang="ts">
import { computed, ref, reactive } from "vue";
import { notNull } from "@/helpers/utility/Type";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import SortableList from "@core/ui/SortableList.vue";

import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useElements } from "@editor/items/element/elements";

import type { ActionCostItem, ActionCostCopyItem } from "./ActionCostItem";
import type { Handleable } from "@core/types/Events";
import { splitUid } from "@/helpers/utility/Id";

const props = defineProps<{
  actionId: number;
  costs: ActionCostItem[];
}>();

const emit = defineEmits<{
  (event: "update:costs", costs: ActionCostItem[]): void;
  (event: "copy", cost: ActionCostCopyItem): void;
}>();

const focussedCost = ref<ActionCostItem | null>(null);

const contextMenuData = reactive({
  cost: null as null | ActionCostItem,
  position: {
    x: 0,
    y: 0
  }
});

const focusButtonRefs = ref<Record<string, HTMLElement | null>>({});
type ExpressionInputElement = InstanceType<typeof ExpressionInput>;
const costExpressionFields = ref<Record<string, ExpressionInputElement | null>>({});

const targets = computed(() => useTargetStore().items);
const resources = computed(() => useResourceStore().items);
const indices = computed(() => useIndexStore().items);

const elements = useElements();

const action = computed(() => useActionStore().items[props.actionId]);

function updateImpulseExpression(
  cost: ActionCostItem,
  value: string
) {
  const costs = props.costs;
  const index = costs.findIndex((c) => c.elementUid === cost.elementUid);
  if (index == -1) return;
  const newCosts = [...costs];
  const newCost = { ...cost, expression: value };
  newCosts[index] = newCost;
  emit("update:costs", newCosts);
}

function deleteCost(cost: ActionCostItem) {
  const newCosts = [...props.costs];
  const index = newCosts.findIndex((c) => c.elementUid === cost.elementUid);
  if (index == -1) return;
  newCosts.splice(index, 1);
  emit("update:costs", newCosts);
}

const affectedItems = computed(() => {
  return props.costs
    .map((cost) => {
      return elements.byUid[cost.elementUid];
    })
    .filter(notNull);
});

async function onAffectedItemChoose(item: {
  type: "target" | "resource";
  id: number;
}) {
  const cost: ActionCostItem = {
    elementUid: `${item.type}_${item.id}`,
    expression: ""
  };
  emit("update:costs", [...props.costs, cost]);
  focusCostExpressionField(cost);
}
function onAffectedItemsChoose(
  items: { type: "target" | "resource"; id: number }[]
) {
  const costs = items.map((item): ActionCostItem => ({
    elementUid: `${item.type}_${item.id}`,
    expression: ""
  })).filter((cost) => !props.costs.some((c) => c.elementUid === cost.elementUid));

  emit("update:costs", [...props.costs, ...costs]);
}

function onCostClick(
  mouseEvent: MouseEvent & Handleable,
  cost: ActionCostItem | null
) {
  if (mouseEvent.hasBeenHandled || mouseEvent.defaultPrevented) {
    return;
  }
  mouseEvent.preventDefault();
  const focusButton = cost ? focusButtonRefs.value[cost.elementUid] : null;
  focusButton?.focus?.();
}

function onCostContextMenu(event: PointerEvent, cost: ActionCostItem) {
  event.preventDefault();
  contextMenuData.cost = cost;
  if (event.pointerType === "") {
    // triggered by keyboard
    if (event.target instanceof HTMLElement) {
      const targetPos = event.target.getBoundingClientRect();
      contextMenuData.position.x = targetPos.x + 5;
      contextMenuData.position.y = targetPos.y + 5;
    }
  } else {
    contextMenuData.position.x = event.clientX;
    contextMenuData.position.y = event.clientY;
  }
}

/**
 * @param cost - the cost to delete
 */
function onContextmenuDelete(cost: ActionCostItem | null) {
  if (cost == null) return;
  contextMenuData.cost = null;
  deleteCost(cost);
}

function toClipboard(costItem: ActionCostItem | null) {
  if (costItem == null) return;
  emit("copy", {
    ...costItem,
    type: "actionCost"
  });
}

function focusCostExpressionField(cost: ActionCostItem) {
  costExpressionFields.value[cost.elementUid]?.focus();
}

function updateOrder(newOrder: string[]) {
  const newCosts = newOrder.map((uid) => props.costs.find((c) => c.elementUid === uid)!);
  emit("update:costs", newCosts);
}

defineExpose({
  focusCostExpressionField
});
</script>

<style lang="scss">
.costlist {
  &__line {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: minmax(2rem, 1fr) min-content;
    gap: 0.5em;
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: 0.5em;
  }

  &__entry {
    border: none;
    background-color: transparent;
    padding: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-text-medium);
    font-weight: 600;
    min-width: 0;

    &:focus-visible {
      outline: none;
    }
  }

  &__input {
    min-width: 8rem;
    width: 100%;
  }

  &__summary {
    font-style: italic;
    font-size: 13px;
    border: none;
    background-color: transparent;
    padding: 0.6em 0;
    color: var(--color-text-medium);

    &:hover {
      color: var(--color-text);
    }
  }
}

.cost-item {
  background-color: rgba(var(--tint-contrast), 0.01);
  border: 1px solid transparent;
  padding: 0.5rem;
  border-radius: 3px;
  box-shadow: 0 1px 5px var(--color-shadow-2);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  outline: 1px solid transparent;
  transition: outline-color 100ms ease;

  &.-focussed {
    outline: 1px dashed var(--color-action);
  }

  &.-selected {
    border-color: var(--color-action);
    background-color: var(--color-background-close);
  }

  &.-focussed.-selected {
    outline: none;
  }

  &:hover {
    background-color: var(--color-background-close);
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  }

  &.-disabled:hover {
    background-color: rgba(var(--tint-contrast), 0.01);
    box-shadow: 0 1px 5px var(--color-shadow-2);
  }

  &__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) min-content min-content;
    grid-template-areas: "text docAnchor context";
    gap: 0.25rem;
    min-width: 0;
    max-width: 100%;
    align-items: center;
  }

  &__title {
    border: none;
    background-color: transparent;
    padding: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-text-medium);
    font-weight: 600;
    min-width: 0;

    &:focus-visible {
      outline: none;
    }
  }

  &__titletext {
    display: block;
    overflow: hidden;
    hyphens: auto;
    font-size: inherit;
    text-align: left;
    text-overflow: ellipsis;
  }

  &__docanchor {
    font-size: 1em;
  }
  &__contextbutton {
    padding: 0.25rem;
  }

  &__background {
    background-color: var(--color-background-deep);
  }
}
</style>
